import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { type LatenessConfigResponse } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { ProgressCarousel } from "@src/appV2/redesign/components/ProgressCarousel";
import { TopLeftBackButton } from "@src/appV2/redesign/components/TopLeftBackButton";
import { Text } from "@src/appV2/redesign/system/ui-components";

import { EducationExampleCard, type ExampleItem } from "./EducationExampleCard";
import { EducationStepNavigation } from "./EducationStepNavigation";

interface ArriveOnTimeStepProps {
  onNext: () => void;
  onBack: () => void;
  pointsForTimeliness: number;
  latenessPolicy: LatenessConfigResponse;
  progressCarousel?: {
    currentIndex: number;
    totalSteps: number;
    onIndexChange: (index: number) => void;
  };
}

function buildExampleRows(latenessPolicy: LatenessConfigResponse): ExampleItem[] {
  const exampleRows: ExampleItem[] = [];

  // we only want to use the startFrom - stopAt verbiage if both values are present - otherwise use startFrom+
  // eslint-disable-next-line prefer-destructuring
  const firstPolicy = latenessPolicy[0];
  // eslint-disable-next-line prefer-destructuring
  const secondPolicy = latenessPolicy[1];

  if (!isDefined(firstPolicy)) {
    return exampleRows;
  }

  if (firstPolicy.minutesLateRange.startFrom && firstPolicy.minutesLateRange.stopAt) {
    exampleRows.push({
      label: `Arrive ${firstPolicy.minutesLateRange.startFrom} - ${firstPolicy.minutesLateRange.stopAt} minutes late`,
      points: `- ${Math.abs(firstPolicy.points)}`,
      isPositive: false,
    });

    if (isDefined(secondPolicy) && secondPolicy.minutesLateRange.startFrom) {
      exampleRows.push({
        label: `Arrive ${secondPolicy.minutesLateRange.startFrom}+ minutes late`,
        points: `- ${Math.abs(secondPolicy.points)}`,
        isPositive: false,
      });
    }
  } else {
    exampleRows.push({
      label: `Arrive ${firstPolicy.minutesLateRange.startFrom}+ minutes late`,
      points: `- ${Math.abs(firstPolicy.points)}`,
      isPositive: false,
    });
  }

  return exampleRows;
}

export function ArriveOnTimeStep(props: ArriveOnTimeStepProps) {
  const { onNext, onBack, pointsForTimeliness, latenessPolicy, progressCarousel } = props;

  const examples: ExampleItem[] = [
    { label: "Arrive on time", points: `+ ${pointsForTimeliness}`, isPositive: true },
  ];

  examples.push(...buildExampleRows(latenessPolicy));

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ width: "100%" }}>
        <TopLeftBackButton iconType="chevron-left" onClick={onBack} />
      </Box>

      <Box
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "32%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "100%",
            paddingX: 2,
          }}
        >
          <EducationExampleCard examples={examples} />
        </Box>

        <Box
          sx={{
            position: "absolute",
            top: "65%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <Stack spacing={6} alignItems="center" sx={{ width: "100%", maxWidth: 343 }}>
            <Text
              variant="h4"
              sx={(theme) => ({
                fontSize: "32px",
                color: theme.palette.text.primary,
                lineHeight: "35px",
                textAlign: "center",
              })}
            >
              Being on time pays off
            </Text>
            <Text
              variant="body1"
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                fontSize: "16px",
                lineHeight: "17px",
                textAlign: "center",
              })}
            >
              On-time arrivals help ensure a safe and smooth shift change. To grow your score
              faster, plan for traffic and aim to arrive early!
            </Text>
          </Stack>
        </Box>

        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
          }}
        >
          <Stack spacing={2} sx={{ width: "100%" }}>
            {progressCarousel && (
              <Box sx={{ paddingX: 2 }}>
                <ProgressCarousel
                  currentIndex={progressCarousel.currentIndex}
                  totalCount={progressCarousel.totalSteps}
                  onIndexChange={progressCarousel.onIndexChange}
                />
              </Box>
            )}
            <EducationStepNavigation onNext={onNext} onBack={onBack} />
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
